<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
    :style="data?.fields?.cssOverride as StyleValue"
  >
    <div :class="$style.content">
      <div :class="$style.textContainer">
        <BaseHeading v-if="data?.fields?.header" as="h2" :class="$style.header">
          <span v-html="data.fields.header" />
        </BaseHeading>
        <p v-if="data?.fields?.subheader" :class="$style.subheader">
          {{ data.fields.subheader }}
        </p>
        <MarkedParser
          v-if="data?.fields?.copy"
          :class="$style.copy"
          :md="data.fields.copy"
        />
      </div>

      <div :class="$style.formContainer">
        <form :class="$style.form" @submit.prevent>
          <div :class="$style.emailWrap">
            <label :for="emailId" :class="$style.visuallyHidden">
              {{ data?.fields?.inputPlaceholder || 'Email address' }}
            </label>
            <input
              :id="emailId"
              :class="$style.input"
              type="email"
              autocomplete="email"
              inputmode="email"
              required
              :placeholder="
                data?.fields?.inputPlaceholder || 'Enter your email address'
              "
            />
            <fieldset
              v-if="data?.fields?.cta"
              disabled
              inert
              :class="$style.submitWrap"
            >
              <CallToAction :cta="data.fields.cta" />
            </fieldset>
          </div>

          <div v-if="data?.fields?.checkboxText" :class="$style.consent">
            <input
              :id="consentId"
              type="checkbox"
              required
              :aria-labelledby="consentTextId"
            />
            <MarkedParser
              :id="consentTextId"
              :class="$style.checkboxText"
              :md="data.fields.checkboxText"
            />
          </div>
        </form>

        <CallToAction
          v-if="data?.fields?.declineCta"
          :cta="data.fields.declineCta"
          :class="$style.declineCta"
        />
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeEmailCaptureWithoutUnresolvableLinksResponse } from '~/types/contentful'
import type { StyleValue } from 'vue'

defineProps<{
  data: TypeEmailCaptureWithoutUnresolvableLinksResponse
}>()

const id = useId()
const emailId = `newsletter-${id}-email`
const consentId = `newsletter-${id}-consent`
const consentTextId = `newsletter-${id}-consent-text`
</script>

<style lang="scss" module>
.config {
  --newsletter-padding: 30px 15px;
  --newsletter-background: var(--white);
  --newsletter-text-color: var(--gray-8);
  --newsletter-header-color: var(--newsletter-text-color);
  --newsletter-copy-color: var(--newsletter-text-color);
  --newsletter-input-color: var(--newsletter-text-color);
  --newsletter-input-background: var(--white);
  --newsletter-input-border: 1px solid var(--gray-5);
  --newsletter-link-color: var(--purple);

  @include media('md') {
    --newsletter-padding: 60px 30px;
  }
}

.wrap {
  padding: var(--newsletter-padding);
  color: var(--newsletter-text-color);
  background: var(--newsletter-background);
  text-align: center;
}

.content {
  display: grid;
  gap: 3em;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;

  @include media('md') {
    grid-template-columns: minmax(0, 1fr) minmax(0, 600px);
    text-align: left;
  }
}

.textContainer {
  min-width: 0;
}

.header {
  @include fluid-type(30px, 45px);
  color: var(--newsletter-header-color);
  line-height: 1;
  max-width: 640px;
  margin: 0 auto 0.25em;
  text-wrap: balance;

  @include media('md') {
    margin-left: 0;
  }
}

.subheader {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.75em;
}

.copy {
  color: var(--newsletter-copy-color);
  max-width: 600px;
  margin: 0 auto;

  @include media('md') {
    margin-left: 0;
  }
}

.formContainer {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.form {
  width: 100%;
}

.emailWrap {
  display: grid;
  gap: 12px;

  @include media('sm') {
    grid-template-columns: minmax(0, 1fr) auto;
  }
}

.input {
  width: 100%;
  min-width: 0;
  min-height: 45px;
  padding: 0.75em 1.5em;
  color: var(--newsletter-input-color);
  background: var(--newsletter-input-background);
  border: var(--newsletter-input-border);
  border-radius: 5px;
  font: inherit;

  &::placeholder {
    color: inherit;
    opacity: 0.8;
  }
}

.submitWrap {
  border: 0;
  margin: 0;
  padding: 0;
}

.submitWrap :global(button),
.submitWrap :global(a) {
  align-items: center;
  height: 100%;
}

.consent {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  margin: 1em 0 0;
  text-align: left;
}

.consent input {
  margin-top: 0.35em;
}

.checkboxText {
  font-size: 14px;
  line-height: 1.5;
}

.checkboxText :global(p) {
  margin: 0;
}

.checkboxText :global(a) {
  color: var(--newsletter-link-color);
}

.declineCta {
  margin-top: 1em;
}

.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
